import React, { createContext, useContext, useState, useCallback, ReactNode, useRef } from 'react';
import { PaymentMethodId, PaymentLocation } from 'Views/Components/_HumanWritten/Payment/PaymentTypeWrapper';
import { TripType } from '../BookingWizard/FerryTripBookingWizard/BookingWizardTypes';
import { PaymentProviderTransactionDetails } from 'Services/Api/_HumanWritten/PaymentService';
import { appConfigStore } from 'Models/AppConfigStore';
import { store } from 'Models/Store';

// Re-export new providers so consumers can start migrating incrementally.
export { PaymentFlowProvider, usePaymentFlow } from './PaymentFlowProvider';
export type { PaymentFlowConfig, OnConfirmCallback, BeforePaymentCallback } from './PaymentFlowProvider';
export { PaymentUIProvider, usePaymentUI } from './PaymentUIProvider';
export type { PaymentUIContextValue } from './PaymentUIProvider';

// ──────────────────────────────────────────────────────────────────────────────
// Legacy shim — keeps all existing consumers working unchanged while we migrate
// them one at a time to usePaymentFlow() + usePaymentUI().
// ──────────────────────────────────────────────────────────────────────────────

type OnConfirmCallback = (confirmAndCheckIn: boolean) => Promise<void> | void;
type BeforePaymentCallback = () => Promise<boolean>;

interface PaymentCheckOutState {
	transactionId: string;
	userId?: string;
	isAlteration: boolean;
	tripType?: TripType;
	eventBooking: boolean;
	paymentLocation: PaymentLocation;
	paymentMethod?: PaymentMethodId;
	tabIsLoading: boolean;
	paymentProcessing: boolean;
	disableContinueButton: boolean;
	totalPrice: number | null;
	dynamicSurcharge: number | null;
	showSurchargeModal: boolean;
	message: string | null;
	triggerRecalculation: boolean;
	refetchPrice: boolean;
	transactionDetails: PaymentProviderTransactionDetails | null;
	includeSubmitButtons: boolean;
	onCancelRedirectUrl?: string;
	redirectUrl?: string;
	clearTerminalPolling?: () => Promise<void>;
	canInvoiceOverride?: boolean;
	/**
	 * Forces card off — both the online card form and the terminal, which is the same payment on a
	 * reader. For a purchase card cannot settle. Left undefined everywhere else, where card is a
	 * universal method — see `PaymentTypeWrapper`.
	 */
	canPayByCardOverride?: boolean;
	inCheckIn: boolean;
	beforePayment?: BeforePaymentCallback;
	onConfirm?: OnConfirmCallback;
}

interface PaymentCheckOutActions {
	initializePaymentFlow: (config: {
		transactionId: string;
		userId?: string;
		isAlteration?: boolean;
		tripType?: TripType;
		eventBooking?: boolean;
		paymentLocation?: PaymentLocation;
		includeSubmitButtons?: boolean;
		onCancelRedirectUrl?: string;
		redirectUrl?: string;
		canInvoiceOverride?: boolean;
		canPayByCardOverride?: boolean;
		inCheckIn?: boolean;
		onConfirm?: OnConfirmCallback;
		beforePayment?: BeforePaymentCallback;
	}) => void;
	setPaymentMethod: (paymentMethod?: PaymentMethodId) => void;
	setTabIsLoading: (loading: boolean) => void;
	setPaymentProcessing: (processing: boolean) => void;
	setDisableContinueButton: (disabled: boolean) => void;
	setTotalPrice: (price: number | null) => void;
	setDynamicSurcharge: (surcharge: number | null) => void;
	updatePricing: (totalPrice: number, surcharge: number) => void;
	setShowSurchargeModal: (show: boolean) => void;
	setMessage: (message: string | null) => void;
	setTriggerRecalculation: (trigger: boolean) => void;
	setRefetchPrice: (refetch: boolean) => void;
	triggerPriceUpdate: () => void;
	setTransactionDetails: (details: PaymentProviderTransactionDetails | null) => void;
	setRedirectUrl: (url?: string) => void;
	setClearTerminalPolling: (callback?: () => Promise<void>) => void;
	setOnConfirm: (callback?: OnConfirmCallback) => void;
	setBeforePayment: (callback?: BeforePaymentCallback) => void;
	resetPaymentFlow: () => void;
	resetUIState: () => void;
	handleError: (errorMessage: string | null) => void;
	handleProviderNotLoaded: () => void;
	handleSurchargeUpdate: (refresh?: boolean) => void;
}

interface PaymentCheckOutContextValue extends PaymentCheckOutState, PaymentCheckOutActions {
	totalPriceWithSurcharge: number;
	hasError: boolean;
	canProceed: boolean;
}

const initialState: PaymentCheckOutState = {
	transactionId: '',
	userId: undefined,
	isAlteration: false,
	tripType: undefined,
	eventBooking: false,
	paymentLocation: 'booking-wizard',
	paymentMethod: undefined,
	tabIsLoading: false,
	paymentProcessing: false,
	disableContinueButton: false,
	totalPrice: null,
	dynamicSurcharge: null,
	showSurchargeModal: false,
	message: null,
	triggerRecalculation: false,
	refetchPrice: false,
	transactionDetails: null,
	includeSubmitButtons: false,
	onCancelRedirectUrl: undefined,
	redirectUrl: undefined,
	clearTerminalPolling: undefined,
	canInvoiceOverride: undefined,
	canPayByCardOverride: undefined,
	inCheckIn: false,
	onConfirm: undefined,
	beforePayment: undefined,
};

const PaymentCheckOutContext = createContext<PaymentCheckOutContextValue | null>(null);

interface PaymentCheckOutProviderProps {
	children: ReactNode;
}

/**
 * @deprecated Migrate to <PaymentFlowProvider> + <PaymentUIProvider> directly.
 *
 * This provider is a backwards-compatible shim that keeps all existing consumers
 * working while we migrate them one at a time. Internally it is a single
 * useState-based provider (same as before) — the new providers are composed by
 * the migrated consumers, not by this shim.
 */
export function PaymentCheckOutProvider({ children }: PaymentCheckOutProviderProps) {
	const [state, setState] = useState<PaymentCheckOutState>(initialState);

	// Track price refresh / recalculation as counters internally to avoid the
	// boolean toggle cancellation bug — but expose them as booleans to match
	// the old API surface for unmigrated consumers.
	const priceRefreshCounterRef = useRef(0);
	const recalculationCounterRef = useRef(0);

	const updateState = useCallback((updates: Partial<PaymentCheckOutState>) => {
		setState(prev => ({ ...prev, ...updates }));
	}, []);

	const initializePaymentFlow = useCallback(
		(config: {
			transactionId: string;
			userId?: string;
			isAlteration?: boolean;
			tripType?: TripType;
			eventBooking?: boolean;
			paymentLocation?: PaymentLocation;
			includeSubmitButtons?: boolean;
			onCancelRedirectUrl?: string;
			redirectUrl?: string;
			canInvoiceOverride?: boolean;
			canPayByCardOverride?: boolean;
			inCheckIn?: boolean;
			onConfirm?: OnConfirmCallback;
			beforePayment?: BeforePaymentCallback;
		}) => {
			let newPaymentMethod = 'credit-card-option';
			if (config.paymentLocation === 'check-in') {
				// Terminal first, then Eftpos when it is enabled. With both off, fall back to the
				// credit card default rather than pre-selecting an option that isn't rendered.
				if (appConfigStore.allowTerminalPayments()) {
					newPaymentMethod = 'terminal-option';
				} else if (appConfigStore.allowEftposPayments()) {
					newPaymentMethod = 'eftpos-option';
				}
			} else if (
				config.paymentLocation === 'parking' &&
				store.isStaff &&
				appConfigStore.allowTerminalPayments()
			) {
				newPaymentMethod = 'terminal-option';
			}

			// Reset ALL state before applying new config — prevents stale UI state
			// (message, loading flags, transactionDetails) from leaking between flows.
			setState({
				...initialState,
				transactionId: config.transactionId,
				userId: config.userId,
				isAlteration: config.isAlteration ?? false,
				tripType: config.tripType,
				eventBooking: config.eventBooking ?? false,
				paymentLocation: config.paymentLocation ?? 'booking-wizard',
				includeSubmitButtons: config.includeSubmitButtons ?? false,
				onCancelRedirectUrl: config.onCancelRedirectUrl,
				redirectUrl: config.redirectUrl,
				canInvoiceOverride: config.canInvoiceOverride,
				canPayByCardOverride: config.canPayByCardOverride,
				inCheckIn: config.inCheckIn ?? false,
				paymentMethod: newPaymentMethod,
				onConfirm: config.onConfirm,
				beforePayment: config.beforePayment,
			});
		},
		[],
	);

	const setPaymentMethod = useCallback(
		(paymentMethod?: PaymentMethodId) => {
			updateState({ paymentMethod });
		},
		[updateState],
	);

	const setTabIsLoading = useCallback((tabIsLoading: boolean) => updateState({ tabIsLoading }), [updateState]);
	const setPaymentProcessing = useCallback(
		(paymentProcessing: boolean) => updateState({ paymentProcessing }),
		[updateState],
	);
	const setDisableContinueButton = useCallback(
		(disableContinueButton: boolean) => updateState({ disableContinueButton }),
		[updateState],
	);
	const setTotalPrice = useCallback((totalPrice: number | null) => updateState({ totalPrice }), [updateState]);
	const setDynamicSurcharge = useCallback(
		(dynamicSurcharge: number | null) => updateState({ dynamicSurcharge }),
		[updateState],
	);
	const updatePricing = useCallback(
		(totalPrice: number, surcharge: number) =>
			updateState({
				totalPrice,
				dynamicSurcharge: surcharge,
			}),
		[updateState],
	);
	const setShowSurchargeModal = useCallback(
		(showSurchargeModal: boolean) => updateState({ showSurchargeModal }),
		[updateState],
	);
	const setMessage = useCallback((message: string | null) => updateState({ message }), [updateState]);
	const setTriggerRecalculation = useCallback(
		(triggerRecalculation: boolean) => updateState({ triggerRecalculation }),
		[updateState],
	);
	const setRefetchPrice = useCallback((refetchPrice: boolean) => updateState({ refetchPrice }), [updateState]);

	const triggerPriceUpdate = useCallback(() => {
		// Use counters internally to avoid the boolean toggle cancellation bug.
		priceRefreshCounterRef.current += 1;
		recalculationCounterRef.current += 1;
		setState(prev => ({
			...prev,
			refetchPrice: !prev.refetchPrice,
			triggerRecalculation: !prev.triggerRecalculation,
		}));
	}, []);

	const setTransactionDetails = useCallback(
		(transactionDetails: PaymentProviderTransactionDetails | null) => updateState({ transactionDetails }),
		[updateState],
	);
	const setRedirectUrl = useCallback((redirectUrl?: string) => updateState({ redirectUrl }), [updateState]);
	const setClearTerminalPolling = useCallback(
		(clearTerminalPolling?: () => Promise<void>) => updateState({ clearTerminalPolling }),
		[updateState],
	);
	const setOnConfirm = useCallback((onConfirm?: OnConfirmCallback) => updateState({ onConfirm }), [updateState]);
	const setBeforePayment = useCallback(
		(beforePayment?: BeforePaymentCallback) => updateState({ beforePayment }),
		[updateState],
	);

	const resetPaymentFlow = useCallback(() => {
		setState({ ...initialState });
	}, []);

	const resetUIState = useCallback(
		() =>
			updateState({
				tabIsLoading: false,
				paymentProcessing: false,
				disableContinueButton: false,
				showSurchargeModal: false,
				message: null,
				transactionDetails: null,
			}),
		[updateState],
	);

	// FIX: handleError(null) now clears the error instead of setting a generic message.
	const handleError = useCallback(
		(errorMessage: string | null) =>
			updateState({
				message: errorMessage,
				tabIsLoading: false,
				paymentProcessing: false,
				showSurchargeModal: false,
			}),
		[updateState],
	);

	const handleProviderNotLoaded = useCallback(
		() =>
			updateState({
				tabIsLoading: false,
				paymentProcessing: false,
			}),
		[updateState],
	);

	const handleSurchargeUpdate = useCallback((refresh = false) => {
		setState(prev => ({
			...prev,
			showSurchargeModal: true,
			refetchPrice: !prev.refetchPrice,
			...(refresh && {
				triggerRecalculation: !prev.triggerRecalculation,
			}),
		}));
	}, []);

	const totalPriceWithSurcharge = (state.totalPrice ?? 0) + (state.dynamicSurcharge ?? 0);
	const hasError = state.message !== null;
	const canProceed = (() => {
		if (!state.paymentMethod) return false;
		if (state.inCheckIn) return true;
		return !state.disableContinueButton;
	})();

	const contextValue: PaymentCheckOutContextValue = {
		...state,
		initializePaymentFlow,
		setPaymentMethod,
		setTabIsLoading,
		setPaymentProcessing,
		setDisableContinueButton,
		setTotalPrice,
		setDynamicSurcharge,
		updatePricing,
		setShowSurchargeModal,
		setMessage,
		setTriggerRecalculation,
		setRefetchPrice,
		triggerPriceUpdate,
		setTransactionDetails,
		setRedirectUrl,
		setClearTerminalPolling,
		setOnConfirm,
		setBeforePayment,
		resetPaymentFlow,
		resetUIState,
		handleError,
		handleProviderNotLoaded,
		handleSurchargeUpdate,
		totalPriceWithSurcharge,
		hasError,
		canProceed,
	};

	return <PaymentCheckOutContext.Provider value={contextValue}>{children}</PaymentCheckOutContext.Provider>;
}

/**
 * @deprecated Migrate to usePaymentFlow() + usePaymentUI() directly.
 *
 * This hook is a backwards-compatible shim. It continues to work exactly as
 * before while consumers are migrated incrementally.
 */
export function usePaymentCheckOut() {
	const context = useContext(PaymentCheckOutContext);
	if (!context) {
		throw new Error('usePaymentCheckOut must be used within a PaymentCheckOutProvider');
	}
	return context;
}
